import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";

export default function ChangeApproval() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState("loading"); // loading | ready | approved | declined | done | error
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const respond = useCallback(async (decision) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/changes/${token}/respond`, { decision });
      setState(r.data.status === "approved" ? "approved" : "declined");
    } catch (e) {
      setState("error");
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  useEffect(() => {
    api.get(`/changes/${token}`).then((r) => {
      setInfo(r.data);
      if (r.data.status && r.data.status !== "proposed") {
        setState(r.data.status === "approved" ? "approved" : r.data.status === "declined" ? "declined" : "done");
      } else {
        const d = params.get("decision");
        if (d === "approve") respond("approve");
        else if (d === "decline") respond("decline");
        else setState("ready");
      }
    }).catch(() => setState("error"));
  }, [token, params, respond]);

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center px-6 py-16" data-testid="change-approval-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="font-display font-extrabold text-xl uppercase tracking-tighter">Immersion Festival</p>
          <p className="text-[11px] uppercase tracking-[0.35em] text-gray-500 mt-2">Iceland · 1–5 October 2026</p>
        </div>

        <div className="border border-white/10 bg-[#0C0C0E] p-8">
          {state === "loading" && (
            <div className="text-center py-10 text-gray-500"><Loader2 className="animate-spin mx-auto mb-4" /> Loading…</div>
          )}

          {state === "error" && (
            <div className="text-center py-6">
              <XCircle className="text-[#e07a6c] mx-auto mb-4" size={40} />
              <p className="text-white font-bold mb-2">Link not found</p>
              <p className="text-gray-500 text-sm">This change request is no longer available. Please contact us if you need help.</p>
            </div>
          )}

          {state === "ready" && info && (
            <div>
              <div className="text-center mb-6">
                <CalendarClock className="text-amber-400 mx-auto mb-3" size={36} />
                <p className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-2">Change to Your Booking</p>
                <h1 className="font-display font-extrabold text-2xl">{info.tour_name}</h1>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-6" data-testid="change-message-text">{info.message}</p>
              <div className="border border-white/10 p-4 text-sm mb-7">
                <div className="flex justify-between mb-2"><span className="text-gray-500">Currently</span><span className="text-gray-300">{info.old}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Proposed</span><span className="text-white font-bold">{info.new}</span></div>
              </div>
              <div className="flex gap-3">
                <button data-testid="approve-btn" onClick={() => respond("approve")} disabled={submitting} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={15} />} Approve
                </button>
                <button data-testid="decline-btn" onClick={() => respond("decline")} disabled={submitting} className="flex-1 border border-[#C44536] text-[#e07a6c] hover:bg-[#C44536]/10 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  <XCircle size={15} /> Decline
                </button>
              </div>
            </div>
          )}

          {state === "approved" && (
            <div className="text-center py-6" data-testid="approved-state">
              <CheckCircle2 className="text-emerald-400 mx-auto mb-4" size={44} />
              <p className="text-white font-bold text-lg mb-2">Change approved — thank you!</p>
              <p className="text-gray-400 text-sm">Your booking has been updated{info ? ` to ${info.new}` : ""}. We've emailed you an updated confirmation with a new QR pass.</p>
            </div>
          )}

          {state === "declined" && (
            <div className="text-center py-6" data-testid="declined-state">
              <XCircle className="text-[#e07a6c] mx-auto mb-4" size={44} />
              <p className="text-white font-bold text-lg mb-2">You've declined the change</p>
              <p className="text-gray-400 text-sm">No problem — your original booking stays as it is. Our team will be in touch to find the best option for you.</p>
            </div>
          )}
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">Questions? Reply to your confirmation email and we'll help.</p>
      </div>
    </div>
  );
}
